import React, { useEffect, useState } from 'react'
import { useActivityType } from '../Hooks/ActivityTypeContext.js'
import PropTypes from 'prop-types'
import InputGroup from '../../EmailSubscriptionCenter/SignIn/InputGroup.jsx'
import { postMFA } from '../Data/LoginDatasource.js'
import {
  FORM_NAME_CHALLENGE_CHOICE,
  FORM_NAME_LOGIN_FORM,
} from '../Helpers/LoginHelper.js'
import { makeMfaAnswerCall } from '../Helpers/MfaHelper.js'
import NewUser from '../Static/NewUser.jsx'
import FormNotification from '../../SelfService/FormNotification.jsx'

function OneTimePasscode({
  otpDta,
  handleDisplayChange,
  mode = 'otp',
  loginInputHook,
  showNewUser = true,
}) {
  const [oneTimePasscodeInput, setOneTimePasscodeInput] = useState('')
  const [formValidation, setFormValidation] = useState({
    'login-one-time-passcode': {
      error: false,
      errorMessage:
        'The passcode you entered is incorrect. Please check the email you received and try again.',
    },
  })
  const [loginInput] = loginInputHook
  const [formErrorFlag, setFormErrorFlag] = useState(false)
  const [formErrorMessage, setFormErrorMessage] = useState('')

  const activityType = useActivityType()?.activityType || null

  const RESPONSE_CODES = {
    CREDENTIALS_NOT_ACCEPTED: '7001-3023',
    SSO_LOCKED: '7001-2029',
    BLOCKED_MFA: '7001-3027',
    LOCKED_MFA: '7001-3027',
    OTP_EXPIRED: '7001-3024',
    FYP_INVALID_CODE: 'CTN_FORGOTPASSWORD_1117',
  }

  useEffect(() => {
    if (otpDta) {
      postMFA(otpDta, activityType)
    }
  }, [otpDta])

  const onChangeOneTimePasscodeInput = (event) => {
    setOneTimePasscodeInput(event.target.value)
  }

  const continueClicked = () => {
    if (!oneTimePasscodeInput) {
      const _formValidation = { ...formValidation }
      _formValidation['login-one-time-passcode'].error = true
      setFormValidation(_formValidation)
    } else {
      const data = {
        otp: oneTimePasscodeInput,
      }
      makeMfaAnswerCall(
        data,
        loginInput,
        mode,
        handleDisplayChange,
        handleError,
        activityType
      )
    }
  }

  const cancelClicked = () => {
    setOneTimePasscodeInput('')
    handleDisplayChange(FORM_NAME_LOGIN_FORM)
  }

  const onDidntReceiveCodeClick = () => {
    setOneTimePasscodeInput('')
    handleDisplayChange(FORM_NAME_CHALLENGE_CHOICE)
  }

  const handleError = function (errorCode) {
    const _formValidation = { ...formValidation }
    switch (errorCode) {
      case RESPONSE_CODES.CREDENTIALS_NOT_ACCEPTED:
      case RESPONSE_CODES.OTP_EXPIRED:
      case RESPONSE_CODES.FYP_INVALID_CODE:
        _formValidation['login-one-time-passcode'].error = true
        _formValidation['login-one-time-passcode'].errorMessage =
          'The passcode you entered is incorrect or has expired. Please check the email you received and try again.'
        setFormValidation(_formValidation)
        break
      case RESPONSE_CODES.SSO_LOCKED:
      case RESPONSE_CODES.BLOCKED_MFA:
      case RESPONSE_CODES.LOCKED_MFA:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'For your protection, your account has been locked because you did not enter your passcode correctly. To unlock your account, please <a href="/about-us/contact-us/">contact a Columbia Threadneedle</a> representative at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a>800.248.2828</a> for assistance.'
        )
        break
      default:
        setFormErrorFlag(true)
        setFormErrorMessage(
          'A problem occured with your account. Please call us at <a aria-label="800. 2 4 8. 2 8 2 8" href="tel: 8002482828">800.248.2828</a> or check your email for further instruction.'
        )
    }
  }

  return (
    <div
      className="u-bgColorLightest u-borderColorNeutralLight u-borderBottom u-borderTop u-borderLeft0 u-borderRight0 u-padding8gu u-marginBottom10gu"
      data-analytics-login-step="Enter OTP"
    >
      <div className="Grid">
        <div className="u-sizeFull u-lg-size5of12 u-lg-after1of12 u-marginBottom6gu u-lg-marginBottom0 Content">
          <h2 className="u-marginTop0 u-marginBottom1gu">
            We&apos;ve sent you a passcode
          </h2>
          <p className="u-marginBottom2gu">
            It may take a few minutes for your passcode to arrive. Enter the
            6-digit passcode we sent to .
          </p>
          <p>Your code:</p>
          <ul className="List u-marginBottom8gu">
            <li className="List-item u-textPrimary u-marginBottom0 u-padding0">
              Expires after 30 minutes
            </li>
            <li className="List-item u-textPrimary u-padding0">
              Does not replace your current password
            </li>
          </ul>
          {formErrorFlag && (
            <FormNotification content={formErrorMessage} isHtml={true} />
          )}
          <InputGroup
            handleInputChange={onChangeOneTimePasscodeInput}
            formValidation={formValidation}
            fieldName="login-one-time-passcode"
            fieldId="login-one-time-passcode"
            placeholder="Enter the passcode from your email"
            label="One-Time Passcode"
            compact={false}
          />

          <div className="Grid Grid--withGutter">
            <div className="u-size6of12">
              <button
                onClick={continueClicked}
                className="Button Button--primary Button--block u-marginRight5gu u-marginBottom3gu u-textSecondary u-textUppercase u-paddingHoriz8gu u-textNoTransform"
                data-analytics="login-otp-continue"
              >
                Continue
              </button>
            </div>
            <div className="u-size6of12">
              <button
                onClick={cancelClicked}
                className="Button Button--primary Button--block u-paddingHoriz8gu u-textNoTransform"
                data-analytics="login-otp-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
          <a
            href="#"
            onClick={onDidntReceiveCodeClick}
            className="Link u-block"
            data-analytics="login-otp-no-code"
          >
            Didn&apos;t receive your code?
          </a>
        </div>
        {showNewUser && (
          <div className="u-sizeFull u-lg-size5of12 u-lg-before1of12 Content">
            <NewUser />
          </div>
        )}
      </div>
    </div>
  )
}

OneTimePasscode.propTypes = {
  handleDisplayChange: PropTypes.func,
}

export default OneTimePasscode
